import { HAZARDS, TUNING } from '@cloudpearl/config';
import type { MapDef } from '@cloudpearl/config';
import { World } from './world';
import type { GroundSample } from './world';
import { ObstacleField, OBSTACLE_CELL, maxReach } from './obstacles';
import type { Obstacle } from './obstacles';
import { GateField } from './gates';
import type { Gate } from './gates';
import { clamp, cosDeg, lerp, saturate } from './math';

/** One tick of player intent. `steer` is an integer in [-TUNING.steerSteps, TUNING.steerSteps]. */
export interface SimInput {
  hold: boolean;
  steer: number;
}

export type LandingQuality = 'perfect' | 'good' | 'rough';
export type DeathCause = 'obstacle' | 'fall' | 'caught';

export type SimEvent =
  | { type: 'launch'; speed: number }
  | { type: 'land'; quality: LandingQuality; combo: number; impact: number; speed: number; airTime: number }
  | { type: 'closeCall'; side: -1 | 1; obstacle: Obstacle }
  | { type: 'gate'; gate: Gate }
  | { type: 'crash'; obstacle: Obstacle }
  | { type: 'fall' }
  | { type: 'caught' }
  | { type: 'milestone'; meters: number }
  | { type: 'dive' }
  | { type: 'hop' };

const SIN_PERFECT = cosDeg(90 - TUNING.perfectAngleDeg);
const SIN_GOOD = cosDeg(90 - TUNING.goodAngleDeg);

/**
 * Deterministic fixed-step simulation of the Cloud Pearl rolling over an open, seeded world.
 * Same (seed, map, inputs) ⇒ bit-identical results on every device.
 */
export class Sim {
  readonly world: World;
  readonly gates: GateField;
  readonly field: ObstacleField;
  readonly map: MapDef;
  readonly dt = 1 / TUNING.tickRate;

  tick = 0;
  time = 0;
  started = false;

  x = 0;
  y = 0;
  z = 0;
  vx = 0;
  vy = 0;
  vz = 0;
  prevX = 0;
  prevY = 0;
  prevZ = 0;

  grounded = true;
  airTime = 0;
  holding = false;
  steer = 0;

  combo = 0;
  bestCombo = 0;
  perfects = 0;
  closeCalls = 0;
  gatesHit = 0;
  hops = 0;
  maxSpeed = 0;
  /** Furthest northward progress (m) — the score. */
  bestProgress = 0;

  /** The storm front's northward progress. */
  stormP = 0;
  stormV = 0;

  alive = true;
  cause: DeathCause | null = null;
  deathObstacle: Obstacle | null = null;
  deathTick = 0;

  /** Ground normal under the pearl (for visuals). */
  nx = 0;
  ny = 1;
  nz = 0;

  readonly events: SimEvent[] = [];

  /** How far beyond its cell a solid can reach and still touch the pearl (largest on the map). */
  private readonly reach: number;
  private nextMilestone = 500;
  private overVoid = false;
  private readonly g: GroundSample = { h: 0, hx: 0, hz: 0, mask: 1 };
  private readonly recentCalls: number[] = [];
  private readonly recentGates: number[] = [];
  private readonly gateScratch: Gate[] = [];

  constructor(seed: number, map: MapDef) {
    this.map = map;
    this.world = new World(seed, map);
    this.gates = new GateField(this.world);
    this.field = new ObstacleField(this.world, this.gates);
    this.reach = Math.max(60, maxReach(map));
    this.reset();
  }

  reset(): void {
    this.tick = 0;
    this.time = 0;
    this.started = false;
    this.x = this.z = 0;
    this.y = this.world.height(0, 0);
    this.vx = this.vy = this.vz = 0;
    this.prevX = this.x;
    this.prevY = this.y;
    this.prevZ = this.z;
    this.grounded = true;
    this.airTime = 0;
    this.holding = false;
    this.steer = 0;
    this.combo = this.bestCombo = this.perfects = this.closeCalls = this.gatesHit = this.hops = 0;
    this.maxSpeed = 0;
    this.bestProgress = 0;
    this.stormP = -this.map.chaser.startGap;
    this.stormV = this.map.chaser.baseSpeed;
    this.alive = true;
    this.cause = null;
    this.deathObstacle = null;
    this.deathTick = 0;
    this.nx = 0;
    this.ny = 1;
    this.nz = 0;
    this.nextMilestone = 500;
    this.overVoid = false;
    this.recentCalls.length = 0;
    this.recentGates.length = 0;
    this.events.length = 0;
  }

  start(): void {
    this.started = true;
    this.vz = -TUNING.startSpeed;
  }

  // ------------------------------------------------------------------ multiplayer hooks
  // Solo runs never call these, so they stay bit-identical and replayable. In a shared run each
  // client simulates its own pearl; these let the network nudge it.

  /** Before start: stand at (x, z) instead of the origin (side-by-side starts). */
  placeAt(x: number, z: number): void {
    if (this.started) return;
    this.x = this.prevX = x;
    this.z = this.prevZ = z;
    this.y = this.prevY = this.world.height(x, z);
  }

  /** Another pearl bumped this one: velocity change plus a small separating shove. */
  bump(dvx: number, dvy: number, dvz: number, dx: number, dz: number): void {
    if (!this.started || !this.alive) return;
    this.vx += dvx;
    this.vz += dvz;
    this.x += dx;
    this.z += dz;
    if (this.grounded) {
      // A hard upward knock pops it off the ground; otherwise it stays glued.
      if (dvy > 4) {
        this.vy += dvy;
        this.grounded = false;
        this.airTime = 0;
      } else this.y = this.world.height(this.x, this.z);
    } else this.vy += dvy;
  }

  /**
   * Co-op: back in the run at (x, z) — on a partner — with their velocity and storm. Progress and
   * stats carry on; the storm keeps a little breathing room so you aren't caught on arrival.
   */
  respawn(x: number, z: number, vx: number, vz: number, stormP: number): void {
    this.alive = true;
    this.cause = null;
    this.deathObstacle = null;
    this.x = this.prevX = x;
    this.z = this.prevZ = z;
    this.y = this.prevY = this.world.height(x, z);
    this.vx = vx;
    this.vy = 0;
    this.vz = vz;
    this.grounded = true;
    this.airTime = 0;
    this.overVoid = false;
    this.combo = 0;
    this.stormP = Math.min(stormP, this.progress - 40);
  }

  get speed(): number {
    return Math.sqrt(this.vx * this.vx + this.vy * this.vy + this.vz * this.vz);
  }

  /** Current northward progress. */
  get progress(): number {
    return -this.z;
  }

  /** Score: furthest progress north, in whole metres. */
  get distance(): number {
    return Math.max(0, Math.floor(this.bestProgress));
  }

  /** 0..1, how close the storm is (1 = about to catch you). */
  get chaserProximity(): number {
    return 1 - saturate((this.progress - this.stormP - 2) / 75);
  }

  get flow(): number {
    return Math.min(this.combo, 5) / 5;
  }

  step(input: SimInput): void {
    this.events.length = 0;
    this.prevX = this.x;
    this.prevY = this.y;
    this.prevZ = this.z;
    this.tick++;
    this.time = this.tick * this.dt;

    if (!this.alive) {
      if (this.cause === 'fall') this.integrateFall();
      return;
    }

    const T = TUNING;
    const hold = input.hold;
    const steer = clamp(Math.round(input.steer), -T.steerSteps, T.steerSteps) / T.steerSteps;
    if (hold && !this.holding) this.events.push({ type: 'dive' });
    this.holding = hold;
    this.steer = steer;

    if (this.grounded) this.stepGround(hold, steer);
    else this.stepAir(hold, steer);
    if (!this.alive) return;

    const sp = this.speed;
    if (sp > T.maxSpeed) {
      const k = T.maxSpeed / sp;
      this.vx *= k;
      this.vy *= k;
      this.vz *= k;
    }
    if (sp > this.maxSpeed) this.maxSpeed = sp;
    if (this.progress > this.bestProgress) this.bestProgress = this.progress;

    this.collide();
    if (!this.alive) return;
    this.checkGates();
    this.updateStorm();
    if (!this.alive) return;

    if (this.bestProgress >= this.nextMilestone) {
      this.events.push({ type: 'milestone', meters: this.nextMilestone });
      this.nextMilestone += 500;
    }
    if ((this.tick & 511) === 0) this.field.prune();
  }

  // ------------------------------------------------------------------ helpers

  /** Horizontal heading (unit) — north when stationary. */
  private heading(out: { ux: number; uz: number; hs: number }): void {
    const hs = Math.sqrt(this.vx * this.vx + this.vz * this.vz);
    out.hs = hs;
    if (hs > 1e-3) {
      out.ux = this.vx / hs;
      out.uz = this.vz / hs;
    } else {
      out.ux = 0;
      out.uz = -1;
    }
  }

  private readonly hd = { ux: 0, uz: -1, hs: 0 };

  /** Sideways (centripetal) acceleration from steering, applied perpendicular to the heading. */
  private turnAccel(steer: number, hs: number): number {
    const T = TUNING;
    return steer * Math.min(T.turnAccelBase + T.turnAccelPerSpeed * hs, T.turnAccelMax);
  }

  // ------------------------------------------------------------------ ground

  private stepGround(hold: boolean, steer: number): void {
    const T = TUNING;
    const dt = this.dt;
    const w = this.world;
    const g = w.sample(this.x, this.z, this.g);
    if (g.mask < 0.5) {
      this.leaveGround();
      this.stepAir(hold, steer);
      return;
    }
    let inv = 1 / Math.sqrt(g.hx * g.hx + 1 + g.hz * g.hz);
    let nx = -g.hx * inv;
    let ny = inv;
    let nz = -g.hz * inv;
    const gEff = T.gravity * (hold ? T.holdGravityMul : 1);
    // Gravity's tangential component (the normal force cancels the rest).
    let ax = gEff * ny * nx;
    let ay = -gEff + gEff * ny * ny;
    let az = gEff * ny * nz;

    const hd = this.hd;
    this.heading(hd);
    const slopeV = g.hx * hd.ux + g.hz * hd.uz;
    const tl = 1 / Math.sqrt(1 + slopeV * slopeV);
    const tx = hd.ux * tl;
    const ty = slopeV * tl;
    const tz = hd.uz * tl;

    if (hold && slopeV < 0) {
      const bonus = T.diveSlopeBonus * T.gravity * -slopeV * tl;
      ax += tx * bonus;
      ay += ty * bonus;
      az += tz * bonus;
    }
    // The pearl always rolls at least at run speed, even uphill: bad landings cost momentum,
    // never the run.
    const run = w.runSpeed(Math.max(0, this.progress));
    if (hd.hs < run) {
      const k = Math.min(1, (run - hd.hs) / 3);
      const uphillPull = Math.max(0, -(ax * tx + ay * ty + az * tz));
      const push = (T.runAccel + uphillPull) * k;
      ax += tx * push;
      ay += ty * push;
      az += tz * push;
    }
    const lat = this.turnAccel(steer, hd.hs);
    ax += -hd.uz * lat;
    az += hd.ux * lat;

    const sp = this.speed;
    if (sp > 1e-3) {
      const k = (T.groundFriction * sp + T.airDrag * sp * sp) / sp;
      ax -= this.vx * k;
      ay -= this.vy * k;
      az -= this.vz * k;
    }

    this.vx += ax * dt;
    this.vy += ay * dt;
    this.vz += az * dt;
    this.x += this.vx * dt;
    this.y += this.vy * dt;
    this.z += this.vz * dt;

    const g2 = w.sample(this.x, this.z, this.g);
    if (g2.mask < 0.5) {
      // Rolled off an edge without a launch.
      this.leaveGround();
      return;
    }
    inv = 1 / Math.sqrt(g2.hx * g2.hx + 1 + g2.hz * g2.hz);
    nx = -g2.hx * inv;
    ny = inv;
    nz = -g2.hz * inv;

    // Launch test: does the ground curve away (along our heading) faster than gravity holds us?
    this.heading(hd);
    const e = 0.75;
    const hA = w.height(this.x - hd.ux * e, this.z - hd.uz * e);
    const hB = w.height(this.x + hd.ux * e, this.z + hd.uz * e);
    const huu = (hA + hB - 2 * g2.h) / (e * e);
    const su = g2.hx * hd.ux + g2.hz * hd.uz;
    const q = 1 + su * su;
    const curvature = -huu / (q * Math.sqrt(q));
    const vAlong2 = hd.hs * hd.hs + this.vy * this.vy;
    if (curvature > 0 && curvature * vAlong2 > gEff * ny) {
      this.leaveGround();
      return;
    }

    // Stay glued: snap and rotate velocity onto the new tangent plane, preserving speed.
    this.y = g2.h;
    const spBefore = this.speed;
    const vn = this.vx * nx + this.vy * ny + this.vz * nz;
    this.vx -= vn * nx;
    this.vy -= vn * ny;
    this.vz -= vn * nz;
    const spAfter = this.speed;
    if (spAfter > 1e-6) {
      const k = spBefore / spAfter;
      this.vx *= k;
      this.vy *= k;
      this.vz *= k;
    }
    this.nx = nx;
    this.ny = ny;
    this.nz = nz;
    this.airTime = 0;
  }

  private leaveGround(): void {
    this.grounded = false;
    this.airTime = 0;
    // Distance over height: cap the climb, keep the speed.
    const cap = TUNING.maxLaunchVy;
    if (this.vy > cap) {
      const sp2 = this.vx * this.vx + this.vy * this.vy + this.vz * this.vz;
      const h2 = this.vx * this.vx + this.vz * this.vz;
      const want = Math.sqrt(Math.max(sp2 - cap * cap, 0));
      const have = Math.sqrt(h2);
      if (have > 1e-6) {
        this.vx *= want / have;
        this.vz *= want / have;
      }
      this.vy = cap;
    }
    this.events.push({ type: 'launch', speed: this.speed });
  }

  // ------------------------------------------------------------------ air

  private stepAir(hold: boolean, steer: number): void {
    const T = TUNING;
    const dt = this.dt;
    const w = this.world;
    const gEff = T.gravity * (hold ? T.holdGravityMul : T.airGravityMul);
    const hd = this.hd;
    this.heading(hd);
    const lat = this.turnAccel(steer, hd.hs) * T.airTurn;
    let ax = -hd.uz * lat;
    let ay = -gEff;
    let az = hd.ux * lat;
    const sp = this.speed;
    if (sp > 1e-3) {
      const k = (T.airDrag * sp * sp) / sp;
      ax -= this.vx * k;
      ay -= this.vy * k;
      az -= this.vz * k;
    }
    this.vx += ax * dt;
    this.vy += ay * dt;
    this.vz += az * dt;
    const wasVoid = this.overVoid;
    this.x += this.vx * dt;
    this.y += this.vy * dt;
    this.z += this.vz * dt;
    this.airTime += dt;

    const g = w.ground(this.x, this.z, this.g);
    if (g.mask < 0.5) {
      this.overVoid = true;
      if (this.y < this.map.terrain.fallY) {
        this.die('fall');
        this.events.push({ type: 'fall' });
      }
      return;
    }
    if (this.y > g.h) {
      this.overVoid = false;
      return;
    }
    // Arriving from open sky well below an island's rim: you hit the cliff face. (Just shy of the
    // lip, we're generous and let you scramble up.)
    if (wasVoid && this.y < g.h - 2.4) {
      this.die('fall');
      this.events.push({ type: 'fall' });
      return;
    }

    // ---- Landing
    const s = w.sample(this.x, this.z, this.g);
    const inv = 1 / Math.sqrt(s.hx * s.hx + 1 + s.hz * s.hz);
    const nx = -s.hx * inv;
    const ny = inv;
    const nz = -s.hz * inv;
    const spIn = this.speed;
    const vn = this.vx * nx + this.vy * ny + this.vz * nz;
    this.vx -= vn * nx;
    this.vy -= vn * ny;
    this.vz -= vn * nz;
    // Slamming into an upslope hurts, but never more than half your speed.
    const spOut = this.speed;
    const floor = spIn * 0.5;
    if (spOut < floor && spOut > 1e-6) {
      const k = floor / spOut;
      this.vx *= k;
      this.vy *= k;
      this.vz *= k;
    }
    this.y = s.h;
    this.grounded = true;
    this.nx = nx;
    this.ny = ny;
    this.nz = nz;
    if (wasVoid) {
      this.overVoid = false;
      this.hops++;
      this.events.push({ type: 'hop' });
    }

    const air = this.airTime;
    this.airTime = 0;
    if (air < T.minAirTime || spIn < 1e-3) return;

    // Downslope along our heading?
    this.heading(hd);
    const slopeV = s.hx * hd.ux + s.hz * hd.uz;
    const sinA = -vn / spIn;
    const impact = saturate(-vn / 28);
    let quality: LandingQuality;
    if (sinA < SIN_PERFECT && slopeV < -0.03) {
      quality = 'perfect';
      const boost = 1 + Math.min(T.perfectBoost + this.combo * T.perfectBoostPerCombo, T.perfectBoostMax);
      this.vx *= boost;
      this.vy *= boost;
      this.vz *= boost;
      this.combo++;
      this.perfects++;
      if (this.combo > this.bestCombo) this.bestCombo = this.combo;
    } else if (sinA < SIN_GOOD) {
      quality = 'good';
    } else {
      quality = 'rough';
      this.combo = 0;
    }
    this.events.push({ type: 'land', quality, combo: this.combo, impact, speed: this.speed, airTime: air });
  }

  private integrateFall(): void {
    const dt = this.dt;
    this.vy -= TUNING.gravity * dt;
    this.vx *= 0.995;
    this.vz *= 0.995;
    this.x += this.vx * dt;
    this.y += this.vy * dt;
    this.z += this.vz * dt;
  }

  // ------------------------------------------------------------------ collisions

  private collide(): void {
    const T = TUNING;
    const pr = T.playerRadius;
    const feet = Math.min(this.prevY, this.y) - pr * 0.4;
    const head = Math.max(this.prevY, this.y) + T.playerHeight;
    // Search every cell that could hold a solid reaching us — mesas are ~100 m across.
    const R = this.reach;
    const ci0 = Math.floor((Math.min(this.prevX, this.x) - R) / OBSTACLE_CELL);
    const ci1 = Math.floor((Math.max(this.prevX, this.x) + R) / OBSTACLE_CELL);
    const cj0 = Math.floor((Math.min(this.prevZ, this.z) - R) / OBSTACLE_CELL);
    const cj1 = Math.floor((Math.max(this.prevZ, this.z) + R) / OBSTACLE_CELL);
    for (let cj = cj0; cj <= cj1; cj++) {
      for (let ci = ci0; ci <= ci1; ci++) {
        const list = this.field.cell(ci, cj);
        for (let i = 0; i < list.length; i++) {
          const o = list[i]!;
          const def = HAZARDS[o.kind];
          const reach = def.footprint * o.scale + 4;
          const ddx = o.x - this.x;
          const ddz = o.z - this.z;
          if (ddx * ddx + ddz * ddz > reach * reach) continue;
          // Player segment in the obstacle's local frame.
          const ax0 = this.prevX - o.x;
          const az0 = this.prevZ - o.z;
          const ax1 = this.x - o.x;
          const az1 = this.z - o.z;
          const l0x = ax0 * o.c + az0 * o.s;
          const l0z = -ax0 * o.s + az0 * o.c;
          const l1x = ax1 * o.c + az1 * o.s;
          const l1z = -ax1 * o.s + az1 * o.c;
          const sc = o.scale;
          let clearance = Infinity;
          for (const p of def.colliders) {
            const oy0 = o.y + p.y0 * sc;
            const oy1 = o.y + p.y1 * sc;
            if (!(head > oy0 && feet < oy1)) continue;
            const cx = p.dx * sc;
            const cz = p.dz * sc;
            if (p.type === 'cyl') {
              const r = p.r * sc + pr;
              if (segCircle(l0x, l0z, l1x, l1z, cx, cz, r)) return this.crash(o);
              const ex = l1x - cx;
              const ez = l1z - cz;
              clearance = Math.min(clearance, Math.sqrt(ex * ex + ez * ez) - r);
            } else {
              const hw = p.hw * sc + pr;
              const hdp = p.hd * sc + pr;
              if (segBox(l0x, l0z, l1x, l1z, cx - hw, cz - hdp, cx + hw, cz + hdp)) return this.crash(o);
              const ex = Math.max(Math.abs(l1x - cx) - hw, 0);
              const ez = Math.max(Math.abs(l1z - cz) - hdp, 0);
              clearance = Math.min(clearance, Math.sqrt(ex * ex + ez * ez));
            }
          }
          if (clearance > 0 && clearance < T.closeCallMargin && this.recentCalls.indexOf(o.id) < 0) {
            this.recentCalls.push(o.id);
            if (this.recentCalls.length > 24) this.recentCalls.shift();
            this.closeCalls++;
            const k = 1 + T.closeCallBoost;
            this.vx *= k;
            this.vz *= k;
            // Which side was it on, relative to our heading?
            this.heading(this.hd);
            const side = (o.x - this.x) * -this.hd.uz + (o.z - this.z) * this.hd.ux > 0 ? 1 : -1;
            this.events.push({ type: 'closeCall', side, obstacle: o });
          }
        }
      }
    }
  }

  private checkGates(): void {
    const list = this.gates.near(this.x, this.z, 30, this.gateScratch);
    for (const g of list) {
      if (this.recentGates.indexOf(g.id) >= 0) continue;
      const d0 = (this.prevX - g.x) * g.nx + (this.prevZ - g.z) * g.nz;
      const d1 = (this.x - g.x) * g.nx + (this.z - g.z) * g.nz;
      if (!(d0 < 0 && d1 >= 0)) continue;
      const t = d0 / (d0 - d1);
      const px = this.prevX + (this.x - this.prevX) * t - g.x;
      const py = this.prevY + (this.y - this.prevY) * t - g.y;
      const pz = this.prevZ + (this.z - this.prevZ) * t - g.z;
      if (px * px + py * py + pz * pz > g.r * g.r) continue;
      this.recentGates.push(g.id);
      if (this.recentGates.length > 16) this.recentGates.shift();
      this.gatesHit++;
      const k = 1 + this.map.gates.boost;
      this.vx *= k;
      this.vz *= k;
      this.stormP -= this.map.gates.stormPush;
      this.events.push({ type: 'gate', gate: g });
    }
  }

  private crash(o: Obstacle): void {
    this.deathObstacle = o;
    this.die('obstacle');
    this.events.push({ type: 'crash', obstacle: o });
  }

  private die(cause: DeathCause): void {
    this.alive = false;
    this.cause = cause;
    this.deathTick = this.tick;
    this.combo = 0;
  }

  // ------------------------------------------------------------------ storm

  private updateStorm(): void {
    const c = this.map.chaser;
    this.stormV = Math.min(c.baseSpeed + c.accel * this.time, c.maxSpeed);
    this.stormP += this.stormV * this.dt;
    const maxLead = lerp(c.maxLeadStart, c.maxLeadEnd, saturate(this.time / c.maxLeadRampTime));
    if (this.progress - this.stormP > maxLead) this.stormP = this.progress - maxLead;
    if (this.stormP >= this.progress - 1) {
      this.die('caught');
      this.events.push({ type: 'caught' });
    }
  }
}

/** Does segment (x0,y0)-(x1,y1) pass within r of (cx,cy)? */
function segCircle(x0: number, y0: number, x1: number, y1: number, cx: number, cy: number, r: number): boolean {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const l2 = dx * dx + dy * dy;
  let t = l2 > 1e-12 ? ((cx - x0) * dx + (cy - y0) * dy) / l2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const px = x0 + dx * t - cx;
  const py = y0 + dy * t - cy;
  return px * px + py * py < r * r;
}

/** Segment vs axis-aligned box (slab test). */
function segBox(x0: number, y0: number, x1: number, y1: number, bx0: number, by0: number, bx1: number, by1: number): boolean {
  let tmin = 0;
  let tmax = 1;
  const dx = x1 - x0;
  const dy = y1 - y0;
  if (Math.abs(dx) < 1e-12) {
    if (x0 < bx0 || x0 > bx1) return false;
  } else {
    let ta = (bx0 - x0) / dx;
    let tb = (bx1 - x0) / dx;
    if (ta > tb) [ta, tb] = [tb, ta];
    tmin = Math.max(tmin, ta);
    tmax = Math.min(tmax, tb);
    if (tmin > tmax) return false;
  }
  if (Math.abs(dy) < 1e-12) {
    if (y0 < by0 || y0 > by1) return false;
  } else {
    let ta = (by0 - y0) / dy;
    let tb = (by1 - y0) / dy;
    if (ta > tb) [ta, tb] = [tb, ta];
    tmin = Math.max(tmin, ta);
    tmax = Math.min(tmax, tb);
    if (tmin > tmax) return false;
  }
  return true;
}
